import Button from 'antd/lib/button';
import Modal from 'antd/lib/modal';
import { useCallback, useState } from 'react';
import { Import, useImportContext } from 'src/components/import';
import { ParsedRecord } from 'src/components/import/ImportParser';
import { usePjmLocationCreateMutation } from 'src/domain/pjm/queries';
import { pjmLocationCsvConfig, PjmLocationCsvColumns, pjmLocationParser } from 'src/domain/pjm/import/pjmLocationParser';
import { mapCsvImportLocationToCreateLocationDto } from 'src/domain/pjm/import/mapCsvImportLocationToCreateLocationDto';
import Alert from 'antd/lib/alert';

interface IPjmLocationsImportModalProps {
    onClose: (actionTaken: boolean) => void;
}

const PjmLocationsImportModalWrapped = ({ onClose }: IPjmLocationsImportModalProps) => {
    const {
        viewState: { view, records },
        setImportProgress,
        importErrors,
        setImportErrors,
        importHasErrors,
    } = useImportContext();

    const [loading, setLoading] = useState<boolean>(false);

    const pjmLocationCreateMutation = usePjmLocationCreateMutation();

    const handleImportSubmit = useCallback(async (records: ParsedRecord<PjmLocationCsvColumns>[]) => {
        if (!records.length) return;

        let imported = 0;
        let failed = 0;
        let total = records.length;
        setImportProgress({ imported, failed, total });

        setLoading(true);
        let row = 0;
        for (let record of records) {
            try {
                const dto = mapCsvImportLocationToCreateLocationDto(record);

                await pjmLocationCreateMutation.mutateAsync(dto);

                imported++;
                setImportProgress({ imported, failed, total });
            } catch (error: any) {
                failed++;
                setImportProgress({ imported, failed, total });
                importErrors[row] = error.message || 'Import failed!';
                setImportErrors(importErrors);
            }
            row++;
        }

        setLoading(false);
    }, []);

    const handleImportCancel = () => {
        const actionTaken = !!records.length;
        onClose(actionTaken);
    };

    const modalActions: JSX.Element[] = [
        ...(importHasErrors ? [<Import.DownloadErrorsButton key="import-download-errors" />] : []),
        <Button key="import-cancel-button" disabled={loading} onClick={handleImportCancel}>
            Close
        </Button>,
        <Import.SubmitButton<PjmLocationCsvColumns> key="import-submit-button" onClick={handleImportSubmit} />,
    ];

    return (
        <Modal
            title="Import PJM Locations"
            width="80%"
            open
            keyboard={false}
            destroyOnHidden
            maskClosable={false}
            onCancel={handleImportCancel}
            footer={modalActions}
        >
            <div>
                {view === 'initial' && <Import.UploadZone acceptFileType=".csv" />}
                {view === 'initial' && <Import.ImportTemplate templateName="import-pjm-locations-template" />}
                {view === 'initial' && (
                    <Alert
                        message="You can completely ignore values for Registration related columns (starting with `Registration Name*` column), if you want to import only location."
                        type="info"
                        showIcon
                        style={{ margin: '20px 0' }}
                    />
                )}
                {view === 'initial' && <Import.FileDescription />}
                {['preview', 'importing', 'completed'].includes(view) && <Import.Preview<PjmLocationCsvColumns> />}
                {['importing', 'completed'].includes(view) && <Import.Progress />}
                {view === 'completed' && <Import.CompletedMessage />}
            </div>
        </Modal>
    );
};

export const PjmLocationsImportModal = (props: IPjmLocationsImportModalProps) => {
    return (
        <Import.Provider<PjmLocationCsvColumns> csvConfig={pjmLocationCsvConfig} parser={pjmLocationParser}>
            <PjmLocationsImportModalWrapped {...props} />
        </Import.Provider>
    );
};
